// "Adjust order" — the finance-side overlay editor for one GPO order (spec §5). Every line is
// editable (quantity / unit price) or can be cancelled on its own; the derived total previews
// what the BE will store unless ops types the total the supplier actually invoiced. The
// platform order is never modified — the originals stay visible next to each input so the
// deviation is always explicit, and "Reset to original" drops the overlay entirely.
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useCustomToast,
} from '@elkaso-app/web-design';
import {
  ReconciliationSupplierOrder,
  useDeleteOrderAdjustmentApi,
  useSaveOrderAdjustmentApi,
} from 'apis/reconciliation';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import {
  AdjustLineDraft,
  computeAdjustedQuantity,
  computeAdjustedTotal,
  getLineError,
  isLineChanged,
  toLineDrafts,
} from 'pages/reconciliation/utils/adjustments';
import { formatAed } from 'pages/reconciliation/utils/format';
import { useEffect, useMemo, useState } from 'react';

export interface IAdjustOrderModal {
  /** null keeps the modal closed (state-as-disclosure, owned by the orders modal). */
  order: ReconciliationSupplierOrder | null;
  onClose: () => void;
  /** Called after a successful save/reset — the parent owns query invalidation. */
  onSaved: () => void;
}

export const AdjustOrderModal = ({ order, onClose, onSaved }: IAdjustOrderModal) => {
  const toast = useCustomToast();
  const [lines, setLines] = useState<AdjustLineDraft[]>([]);
  const [overrideTotal, setOverrideTotal] = useState('');
  const [reason, setReason] = useState('');

  // Re-seed from the row every time a (new) order is opened; closing resets nothing so the
  // exit animation keeps its content.
  useEffect(() => {
    if (!order) return;
    setLines(toLineDrafts(order.items));
    setOverrideTotal(order.adjustment?.isTotalOverridden ? String(order.orderTotal) : '');
    setReason(order.adjustment?.reason ?? '');
  }, [order]);

  const { mutate: saveAdjustment, isLoading: isSaving } = useSaveOrderAdjustmentApi({
    onSuccess: () => {
      toast({ description: 'Order adjustment saved — reconciliation figures now follow it', status: 'success' });
      onSaved();
      onClose();
    },
  });

  const { mutate: deleteAdjustment, isLoading: isResetting } = useDeleteOrderAdjustmentApi({
    onSuccess: () => {
      toast({ description: 'Adjustment removed — the order reconciles at its original figures', status: 'success' });
      onSaved();
      onClose();
    },
  });

  const derivedTotal = useMemo(() => computeAdjustedTotal(lines), [lines]);
  const derivedQuantity = useMemo(() => computeAdjustedQuantity(lines), [lines]);
  const lineErrors = useMemo(() => lines.map(getLineError), [lines]);
  const hasLineError = lineErrors.some(Boolean);
  const overrideNumber = overrideTotal.trim() === '' ? null : Number(overrideTotal);
  const overrideError =
    overrideNumber === null
      ? null
      : Number.isNaN(overrideNumber) || overrideNumber < 0
      ? 'Total must be 0 or more'
      : !/^\d*(\.\d{0,2})?$/.test(overrideTotal)
      ? 'Use at most 2 decimal places'
      : null;
  const effectiveTotal = overrideNumber !== null && !overrideError ? overrideNumber : derivedTotal;
  const changedLines = lines.filter(isLineChanged).length;
  const isBusy = isSaving || isResetting;
  const canSubmit =
    !!order && lines.length > 0 && !hasLineError && !overrideError && reason.trim().length > 0 && !isBusy;

  const updateLine = (orderItemId: number, patch: Partial<AdjustLineDraft>) =>
    setLines((prev) => prev.map((l) => (l.orderItemId === orderItemId ? { ...l, ...patch } : l)));

  const submit = () => {
    if (!order || !canSubmit) return;
    saveAdjustment({
      orderId: order.orderId,
      items: lines.map((l) => ({
        orderItemId: l.orderItemId,
        quantity: l.isCancelled ? 0 : Number(l.quantity),
        price: Number(l.price),
        isCancelled: l.isCancelled,
      })),
      totalOverride: overrideNumber,
      reason: reason.trim(),
    });
  };

  const originalTotal = order?.originalTotal ?? order?.orderTotal ?? 0;

  return (
    <Modal isOpen={!!order} onClose={onClose} size={{ base: 'full', md: '3xl' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb='xs'>
          <Stack spacing='2px'>
            <Text>Adjust order {order?.orderCode}</Text>
            <Text fontSize='sm' fontWeight='normal' color='gray.500'>
              {order?.restaurantName} · reconciliation only — the order itself is not changed
            </Text>
          </Stack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing='md'>
            {lines.length === 0 ? (
              <Alert status='warning' borderRadius='md' fontSize='sm'>
                <AlertIcon />
                This order has no editable lines — you can still type the invoiced total below once lines are available.
              </Alert>
            ) : (
              <TableContainer overflowX='auto' whiteSpace='normal'>
                <Table size='sm' w='100%'>
                  <Thead>
                    <Tr>
                      <Th fontSize='xs' color='gray.400' pl='0'>
                        Item
                      </Th>
                      <Th fontSize='xs' color='gray.400' isNumeric w='140px'>
                        Qty
                      </Th>
                      <Th fontSize='xs' color='gray.400' isNumeric w='150px'>
                        Price (AED)
                      </Th>
                      <Th fontSize='xs' color='gray.400' isNumeric w='120px' whiteSpace='nowrap'>
                        Line total
                      </Th>
                      <Th fontSize='xs' color='gray.400' w='90px'>
                        Cancel
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {lines.map((line, index) => {
                      const error = lineErrors[index];
                      const lineTotal = line.isCancelled ? 0 : computeAdjustedTotal([line]);
                      return (
                        <Tr key={line.orderItemId} opacity={line.isCancelled ? 0.55 : 1}>
                          <Td pl='0' fontSize='sm' wordBreak='break-word'>
                            <Text as='span' textDecoration={line.isCancelled ? 'line-through' : undefined}>
                              {line.nameEn}
                            </Text>
                            <Text fontSize='xs' color='gray.400'>
                              {line.unit} · ordered {line.originalQuantity} × {formatAed(line.originalPrice)}
                            </Text>
                            {error && (
                              <Text fontSize='xs' color='red.500'>
                                {error}
                              </Text>
                            )}
                          </Td>
                          <Td isNumeric>
                            <Input
                              size='sm'
                              textAlign='right'
                              value={line.quantity}
                              isDisabled={line.isCancelled}
                              isInvalid={!!error && /Quantity/.test(error)}
                              onChange={(e) => updateLine(line.orderItemId, { quantity: numOnly(e.target.value) })}
                            />
                          </Td>
                          <Td isNumeric>
                            <Input
                              size='sm'
                              textAlign='right'
                              value={line.price}
                              isDisabled={line.isCancelled}
                              isInvalid={!!error && /Price/.test(error)}
                              onChange={(e) => updateLine(line.orderItemId, { price: numOnly(e.target.value) })}
                            />
                          </Td>
                          <Td isNumeric fontSize='sm' whiteSpace='nowrap'>
                            {formatAed(lineTotal)}
                          </Td>
                          <Td>
                            <Checkbox
                              size='sm'
                              colorScheme='red'
                              isChecked={line.isCancelled}
                              onChange={(e) => updateLine(line.orderItemId, { isCancelled: e.target.checked })}>
                              <Text fontSize='xs'>Cancelled</Text>
                            </Checkbox>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            <HStack justify='space-between' flexWrap='wrap' rowGap='xs' px='sm' py='xs' bg='#FAFBFC' borderRadius='md'>
              <Text fontSize='sm' color='gray.600'>
                Ordered total <strong>{formatAed(originalTotal)}</strong> · derived from lines{' '}
                <strong>{formatAed(derivedTotal)}</strong> ({derivedQuantity} qty
                {changedLines > 0 ? `, ${changedLines} ${changedLines === 1 ? 'line' : 'lines'} changed` : ''})
              </Text>
              <Text fontSize='sm' fontWeight='bold' color='gray.700' whiteSpace='nowrap'>
                Reconciles at {formatAed(effectiveTotal)} AED
              </Text>
            </HStack>

            <FormControl isInvalid={!!overrideError}>
              <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                Total invoice value (optional override)
              </FormLabel>
              <Input
                size='sm'
                maxW='220px'
                value={overrideTotal}
                placeholder={formatAed(derivedTotal)}
                onChange={(e) => setOverrideTotal(numOnly(e.target.value))}
              />
              <FormHelperText fontSize='xs'>
                {overrideError ??
                  'Type the total on the supplier’s invoice when it cannot be explained line by line. Leave empty to use the derived total.'}
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                Reason
              </FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder='e.g. 1 carton short on delivery, price agreed at 18.50'
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <HStack w='100%' justify='space-between' flexWrap='wrap' rowGap='xs'>
            <Box>
              {order?.adjustment && (
                <Button
                  variant='link'
                  size='sm'
                  colorScheme='red'
                  isLoading={isResetting}
                  isDisabled={isSaving}
                  onClick={() => deleteAdjustment({ orderId: order.orderId })}>
                  Reset to original order
                </Button>
              )}
            </Box>
            <HStack spacing='sm'>
              <Button variant='outline' onClick={onClose} isDisabled={isBusy}>
                Cancel
              </Button>
              <Button colorScheme='red' onClick={submit} isDisabled={!canSubmit} isLoading={isSaving}>
                Save adjustment
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
